import { Injectable, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { HttpClient, HttpStatusCode } from '@angular/common/http';
import { NotificationService } from '@services/notification.service';
import { tap, finalize, mergeMap, map } from 'rxjs/operators';
import {
  take,
  Observable,
  catchError,
  throwError,
  interval,
  switchMap,
  of,
  BehaviorSubject,
} from 'rxjs';

import { environment } from '@environment';
import { SessionService } from './session.service';
import { AuthenticatedUser } from '@shared/types/authenticated-user';
import { ClientService } from './client.service';
import {
  ResetUserPassword,
  ChangePasswordPayload,
} from '@shared/types/reset-user-password';
import {
  MfaChallenge,
  MfaSetupRequired,
  MfaSetupResponse,
} from '@shared/types/mfa';
import { UserActionService } from './user-action.service';
import { UserAction } from '@longnecktech/splash-commons-fe';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private http = inject(HttpClient);
  private router = inject(Router);
  private session = inject(SessionService);
  private clientService = inject(ClientService);
  private userActionService = inject(UserActionService);
  private notificationService = inject(NotificationService);

  private destroyRef = inject(DestroyRef);
  private _isLoading = new BehaviorSubject<boolean>(false);

  readonly isLoading$ = this._isLoading.asObservable();

  constructor() {}

  login({
    client,
    email,
    password,
  }: {
    client: string;
    email: string;
    password: string;
  }): Observable<AuthenticatedUser | MfaChallenge | MfaSetupRequired> {
    this.session.resetDataOnLogin();
    return this.http
      .post<AuthenticatedUser | MfaChallenge | MfaSetupRequired>(
        environment.backendUrl + '/api/public/backoffice-auth',
        {
          client,
          email,
          password,
        },
      )
      .pipe(
        mergeMap((result) => {
          if (
            (result as MfaChallenge).mfaRequired ||
            (result as MfaSetupRequired).mfaSetupRequired
          ) {
            return of(result);
          }
          const user = result as AuthenticatedUser;
          return this.userActionService
            .sendAction(UserAction.BACKOFFICE_LOGIN, {}, user.uuid)
            .pipe(map(() => user));
        }),
        tap((result) => {
          const isMfaStep =
            (result as MfaChallenge).mfaRequired ||
            (result as MfaSetupRequired).mfaSetupRequired;
          if (!isMfaStep) {
            this.session.initSession(result as AuthenticatedUser);
            this.fetchUser();
          }
        }),
        catchError((error) => {
          this.resetLocalStorage();
          return throwError(() => error);
        }),
      );
  }

  verifyMfa(preAuthToken: string, code: string): Observable<AuthenticatedUser> {
    return this.http
      .post<AuthenticatedUser>(
        environment.backendUrl + '/api/public/backoffice-auth/mfa/verify',
        { preAuthToken, code },
      )
      .pipe(
        mergeMap((user) =>
          this.userActionService
            .sendAction(UserAction.BACKOFFICE_LOGIN, {}, user.uuid)
            .pipe(map(() => user)),
        ),
        tap((user) => {
          this.session.initSession(user);
          this.fetchUser();
        }),
        catchError((error) => {
          this.resetLocalStorage();
          return throwError(() => error);
        }),
      );
  }

  setupMfaWithToken(preAuthToken: string): Observable<MfaSetupResponse> {
    return this.http.post<MfaSetupResponse>(
      environment.backendUrl + '/api/public/backoffice-auth/mfa/setup',
      { preAuthToken },
    );
  }

  confirmMfaSetup(
    preAuthToken: string,
    code: string,
  ): Observable<AuthenticatedUser> {
    return this.http
      .post<AuthenticatedUser>(
        environment.backendUrl + '/api/public/backoffice-auth/mfa/confirm',
        { preAuthToken, code },
      )
      .pipe(
        mergeMap((user) =>
          this.userActionService
            .sendAction(UserAction.BACKOFFICE_LOGIN, {}, user.uuid)
            .pipe(map(() => user)),
        ),
        catchError((error) => {
          this.resetLocalStorage();
          return throwError(() => error);
        }),
      );
  }

  logout(shouldReload: boolean = true): void {
    this.session.authenticatedUser$
      .pipe(
        take(1),
        mergeMap((user) =>
          this.userActionService.sendAction(
            UserAction.BACKOFFICE_LOGOUT,
            {},
            user?.uuid,
          ),
        ),
        finalize(() => {
          this.resetLocalStorage();
          if (shouldReload) {
            location.reload();
          }
        }),
      )
      .subscribe();
  }

  fetchThirdPartyPreviewToken(data: {
    tags?: string;
    language: string;
    externalId?: string;
    country?: string;
    currency?: string;
    brand?: string;
  }): Observable<{ token: string }> {
    return this.http.post<{ token: string }>(
      environment.backendUrl + '/api/admin/user/third-party-token',
      data,
    );
  }

  changePassword(payload: ChangePasswordPayload): Observable<void> {
    this._isLoading.next(true);

    return this.http
      .put<void>(
        environment.backendUrl + '/api/admin/user/change-password',
        payload,
      )
      .pipe(finalize(() => this._isLoading.next(false)));
  }

  fetchUser() {
    this.http
      .get<AuthenticatedUser>(environment.backendUrl + '/api/admin/user')
      .pipe(
        take(1),
        mergeMap((user) =>
          this.userActionService
            .sendAction(UserAction.BACKOFFICE_FETCH_USER, {}, user.uuid)
            .pipe(map(() => user)),
        ),
      )
      .subscribe({
        next: (user) => {
          this.session.initSession(user);
          this.updateAuthToken();
          this.clientService.fetchClients().pipe(take(1)).subscribe();
        },
        error: (e) => {
          if (e.status !== HttpStatusCode.Forbidden) {
            this.notificationService.showErrorNotification(
              e.message || 'Oops, an error occurred!',
            );
          }
        },
      });
  }

  private updateAuthToken(): void {
    interval(10 * 60 * 1000)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        switchMap(() =>
          this.http.get<AuthenticatedUser>(
            environment.backendUrl + '/api/admin/user',
          ),
        ),
      )
      .subscribe({
        next: (user) => {
          this.session.setAuthToken(user.token);
        },
        error: () => {
          this.resetLocalStorage();
          this.router.navigate(['/login']);
        },
      });
  }

  resetLocalStorage(): void {
    localStorage.removeItem('auth-token');
    localStorage.removeItem('client');
    localStorage.removeItem('gametype');
  }

  resetUserPassword(payload: ResetUserPassword) {
    return this.http.put<ResetUserPassword>(
      environment.backendUrl + '/api/public/backoffice-auth/reset-password',
      payload,
    );
  }
}
